import {
  onBeforeUnmount,
  onMounted,
  toValue,
  type MaybeRefOrGetter,
} from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * Warns before an in-app navigation or a page unload throws away unsaved edits.
 *
 * Call it from the setup of the component that owns the form, passing its own dirty flag. Any
 * component rendered under the route's `<router-view>` may call it, and the guard lives exactly as
 * long as that component: an editor that unmounts on close stops guarding with it. The in-app half
 * asks for confirmation and cancels the navigation on refusal; the unload half can only ask the
 * browser to show its own generic prompt, which is all the platform allows.
 */
export function useUnsavedChangesGuard(
  isDirty: MaybeRefOrGetter<boolean>
): void {
  const { t } = useI18n();

  onBeforeRouteLeave(() => {
    if (!toValue(isDirty)) return true;
    return window.confirm(t("common.unsaved_changes_confirm"));
  });

  const warnOnUnload = (event: BeforeUnloadEvent) => {
    if (!toValue(isDirty)) return;
    // Browsers ignore any custom text here and show their own wording; preventing the default is
    // the whole of the API that still works.
    event.preventDefault();
    event.returnValue = "";
  };

  onMounted(() => window.addEventListener("beforeunload", warnOnUnload));
  onBeforeUnmount(() =>
    window.removeEventListener("beforeunload", warnOnUnload)
  );
}
